import React from 'react';
import styled from 'styled-components';
import {useParams, useHistory} from 'react-router';
import {Container, Draggable, DropResult} from 'react-smooth-dnd';
import {
  useMailCampaignQuery,
  useUpdateMailCampaignBlockPositionMutation,
  useCreateMailCampaignBlockMutation,
} from 'api';
import Block from './Block';

export default () => {
  const {poolId, mailCampaignId} = useParams<{
    poolId: string;
    mailCampaignId: string;
  }>();
  const history = useHistory();

  const [blocks, setBlocks] = React.useState([]);

  const {data: {mailCampaign = null} = {}} = useMailCampaignQuery({
    variables: {uuid: mailCampaignId},
    skip: !mailCampaignId,
  });

  const [updateMailCampaignBlockPosition] =
    useUpdateMailCampaignBlockPositionMutation();

  const [createMailCampaignBlock] = useCreateMailCampaignBlockMutation();

  const onClick = () => history.push(`/mail_campaigns/${mailCampaignId}/mail`);

  const onDragStart = () =>
    history.push(`/mail_campaigns/${mailCampaignId}/mail`);

  const onDrop = async ({removedIndex, addedIndex, payload}: DropResult) => {
    if (addedIndex === null) return;

    if (payload?.type) {
      // New block from toolbar
      if (mailCampaign?.status === 'draft') {
        await createMailCampaignBlock({
          variables: {
            uuid: mailCampaignId,
            type: `Block${payload.type.charAt(0).toUpperCase() + payload.type.slice(1)}`,
            position: addedIndex,
          },
        });
      }
    } else if (removedIndex !== null && removedIndex !== addedIndex) {
      // Reordering existing blocks
      const result = [...blocks];
      const itemToAdd = result.splice(removedIndex, 1)[0];
      result.splice(addedIndex, 0, itemToAdd);
      setBlocks(result);

      if (mailCampaign) {
        await updateMailCampaignBlockPosition({
          variables: {
            uuid: mailCampaign.uuid,
            from: removedIndex,
            to: addedIndex,
          },
        });
      }
    }
  };

  React.useEffect(() => {
    if (mailCampaign) {
      setBlocks(mailCampaign.blocks);
    }
  }, [mailCampaign]);

  return (
    <Background
      style={{backgroundColor: mailCampaign?.backgroundColor}}
      onClick={onClick}>
      <Mail
        style={{
          backgroundColor: mailCampaign?.bodyColor,
          color: mailCampaign?.textColor,
        }}>
        <Container
          groupName="mail-blocks"
          onDragStart={onDragStart}
          onDrop={onDrop}
          lockAxis="y"
          dragHandleSelector=".dragHandle"
          getChildPayload={(index) => blocks[index]}>
          {blocks.map((block) => (
            <Draggable key={block.id}>
              <Block block={block} />
            </Draggable>
          ))}
        </Container>
      </Mail>
    </Background>
  );
};

const Background = styled.div`
  height: calc(100vh - 320px);
  background: #f3f5f7;
  border: 1px solid var(--text-color-3);
  border-radius: 4px;
  flex: 1;
  overflow: auto;
`;

const Mail = styled.div`
  .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
    margin-bottom: 10px;
  }

  .smooth-dnd-draggable-wrapper.dragging {
    opacity: 0.5;
  }
  width: 550px;
  background-color: #fff;

  .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
    overflow: visible;
  }

  /* reset */
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.5;
  padding: 0;
  font-family:
    monospace,
    -apple-system,
    sans-serif;

  div,
  dl,
  dt,
  dd,
  ul,
  ol,
  li,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  pre,
  code,
  form,
  fieldset,
  legend,
  input,
  textarea,
  p,
  blockquote,
  th,
  td {
    margin: 0;
    padding: 0;
    color: inherit;
  }

  table {
    width: 100%;
  }

  table,
  td {
    border-collapse: collapse;
    border-collapse: collapse;
    border-spacing: 0;
  }

  img {
    border: 0 none;
    height: auto;
    line-height: 100%;
    outline: none;
    text-decoration: none;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 100%;
    font-weight: normal;
  }

  h1 {
    font-size: 2em;
  }

  h2 {
    font-size: 1.5em;
  }

  h3 {
    font-size: 1.17em;
  }

  h4 {
    font-size: 1em;
  }

  h5 {
    font-size: 0.83em;
  }

  h6 {
    font-size: 0.67em;
  }

  a {
    text-decoration: underline;
  }

  a img {
    border: 0 none;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }
`;
